/** @jsxRuntime classic */
/** @jsx jsx */
import { useApolloClient } from "@apollo/react-hooks";
import { mq, Pricing, Redirect, SharedLayout, SocialLoginOptions } from "@app/components";
import { colors } from "@app/components/src/__common__/theme";
import { UserAccountType, useRegisterMutation, useSharedQuery } from "@app/graphql";
import {
  extractError,
  formItemLayout,
  getCodeFromError,
  resetWebsocketConnection,
  setPasswordInfo,
  tailFormItemLayout,
} from "@app/lib";
import { css, jsx } from "@emotion/react";
import { Alert, Button, Checkbox, Divider, Form, Input, Radio, Row } from "antd";
import { useForm } from "antd/lib/form/Form";
import { ApolloError } from "apollo-client";
import { NextPage } from "next";
import { useRouter } from "next/router";
import { Store } from "rc-field-form/lib/interface";
import { FocusEvent, useCallback, useEffect, useRef, useState } from "react";

import { isSafe } from "./login";

interface RegisterProps {
  next: string | null;
}

/**
 * The registration page just renders the standard layout and embeds the
 * registration form.
 */
const Register: NextPage<RegisterProps> = ({ next: rawNext }) => {
  const [error, setError] = useState<Error | ApolloError | null>(null);
  const [showRegisterForm, setShowRegisterForm] = useState<boolean>(false);
  const [canCallRegister, setCanCallRegister] = useState<boolean>(true);
  const [_passwordStrength, setPasswordStrength] = useState<number>(0);
  const [_passwordSuggestions, setPasswordSuggestions] = useState<string[]>([]);
  const [priceId, setPriceId] = useState<string | null>(null);
  const pricingRef = useRef<HTMLDivElement>(null);
  const next: string = isSafe(rawNext) ? rawNext! : "/";
  const query = useSharedQuery();
  const router = useRouter();

  const showContinueBtn = router.query.google_new !== "true";

  const [register, { loading }] = useRegisterMutation({});
  const client = useApolloClient();
  const [confirmDirty, setConfirmDirty] = useState(false);
  const [form] = useForm();
  const [accountType, setAccountType] = useState<UserAccountType | null>(null);

  const handleSubmit = useCallback(
    async (values: Store) => {
      try {
        if (canCallRegister && !loading) {
          await register({
            variables: {
              /** Select a username for them to satisfy existing user system */
              username: `usr_${Date.now()}${Math.floor(Math.random() * 100000)}`.slice(0, 23),
              email: values.email,
              password: values.password,
              name: `${values.firstName} ${values.lastName}`,
              accountType: accountType || UserAccountType.Educator,
              studentFacingName: values.studentFacingName,
              registerPricingOption: priceId,
            },
          });
          setCanCallRegister(false);
          // Success: refetch
          resetWebsocketConnection();
          client.resetStore();
          window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
          router.push(`/verify?pid=${priceId}`);
        }
      } catch (e) {
        const code = getCodeFromError(e as any);
        setCanCallRegister(true);
        if (code === "WEAKP") {
          form.setFields([
            {
              name: "password",
              value: form.getFieldValue("password"),
              errors: ["Password must be at least 8 characters long"],
            },
          ]);
        } else if (code === "EMTKN") {
          form.setFields([
            {
              name: "email",
              value: form.getFieldValue("email"),
              errors: ["Email is already in use"],
            },
          ]);
        } else {
          setError(e as any);
        }
      }
    },
    [form, register, client, router, accountType, canCallRegister, loading, priceId]
  );

  const handleConfirmBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setConfirmDirty(confirmDirty || !!value);
    },
    [setConfirmDirty, confirmDirty]
  );

  const compareToFirstPassword = useCallback(
    (_rule: any, value: any, callback: any) => {
      if (value && value !== form.getFieldValue("password")) {
        callback("Make sure your passwords are matching");
      } else {
        callback();
      }
    },
    [form]
  );

  const validateToSAccepted = useCallback((_rule: any, value: any, callback: any) => {
    if (!value) {
      callback("Please read and accept the Terms of Service before registering.");
    } else {
      callback();
    }
  }, []);

  const focusElement = useRef<Input>(null);
  useEffect(() => void (focusElement.current && focusElement.current!.focus()), [focusElement]);

  const [_passwordIsFocussed, setPasswordIsFocussed] = useState(false);
  const [_passwordIsDirty, setPasswordIsDirty] = useState(false);
  const setPasswordFocussed = useCallback(() => {
    setPasswordIsFocussed(true);
  }, [setPasswordIsFocussed]);
  const setPasswordNotFocussed = useCallback(() => {
    setPasswordIsFocussed(false);
  }, [setPasswordIsFocussed]);

  const handleValuesChange = useCallback(
    (changedValues) => {
      setPasswordInfo({ setPasswordStrength, setPasswordSuggestions }, changedValues);
      setPasswordIsDirty(form.isFieldTouched("password"));
    },
    [form]
  );

  const code = getCodeFromError(error);

  return (
    <SharedLayout title="Register" query={query}>
      {({ currentUser }) =>
        currentUser?.isVerified ? (
          <Redirect href={next} />
        ) : (
          <div css={[s.root]}>
            {showRegisterForm ? (
              <Row justify="center">
                <Row justify="center" css={{ width: "100%" }}>
                  <div css={{ fontSize: "32px", fontWeight: 700, marginBottom: 50, marginTop: 50 }}>
                    Create your Account
                  </div>
                </Row>
                <Form
                  {...formItemLayout}
                  name="register-form"
                  form={form}
                  onFinish={handleSubmit}
                  onValuesChange={handleValuesChange}
                >
                  <Row>
                    <Form.Item
                      css={[s.inputItem(true), { alignSelf: "end" }]}
                      label="First Name"
                      name="firstName"
                      colon={false}
                      required={false}
                      rules={[
                        {
                          required: true,
                          message: "First name is required",
                          whitespace: true,
                        },
                      ]}
                    >
                      <Input
                        ref={focusElement}
                        css={{ width: 150 }}
                        autoComplete="given-name"
                        data-cy="registerpage-input-name"
                      />
                    </Form.Item>
                    <Form.Item
                      css={[s.inputItem(true), { marginLeft: 24, alignSelf: "end" }]}
                      label="Last Name"
                      name="lastName"
                      colon={false}
                      required={false}
                      rules={[
                        {
                          required: true,
                          message: "Last name is required",
                          whitespace: true,
                        },
                      ]}
                    >
                      <Input
                        ref={focusElement}
                        css={{ width: 150 }}
                        autoComplete="family-name"
                        data-cy="registerpage-input-name"
                      />
                    </Form.Item>
                  </Row>
                  {accountType === UserAccountType.Educator && (
                    <div>
                      <Form.Item
                        css={[s.inputItem()]}
                        colon={false}
                        required={false}
                        rules={[
                          {
                            required: true,
                            message: "Please enter a student-facing name",
                            whitespace: true,
                          },
                        ]}
                        label={
                          <span data-cy="registerpage-student-name-label">
                            What do your students call you (i.e. Mr. McFadden)?
                          </span>
                        }
                        labelCol={{ style: { maxWidth: "100%" } }}
                        name="studentFacingName"
                      >
                        <Input ref={focusElement} data-cy="registerpage-input-student-name" />
                      </Form.Item>
                    </div>
                  )}
                  <Form.Item
                    label="E-mail"
                    name="email"
                    css={[s.inputItem()]}
                    colon={false}
                    required={false}
                    rules={[
                      {
                        type: "email",
                        message: "The input is not valid E-mail.",
                      },
                      {
                        required: true,
                        message: "Please input your E-mail.",
                      },
                    ]}
                  >
                    <Input data-cy="registerpage-input-email" autoComplete="email" />
                  </Form.Item>
                  <Form.Item label="Password" css={[s.inputItem()]} colon={false} required={false}>
                    <Form.Item
                      noStyle
                      name="password"
                      rules={[
                        {
                          required: true,
                          message: "Please input your password.",
                        },
                      ]}
                    >
                      <Input
                        type="password"
                        autoComplete="new-password"
                        data-cy="registerpage-input-password"
                        onFocus={setPasswordFocussed}
                        onBlur={setPasswordNotFocussed}
                      />
                    </Form.Item>
                    {/* <div css={{ marginBottom: 55 }}>
                      <PasswordStrength
                        passwordStrength={passwordStrength}
                        suggestions={passwordSuggestions}
                        isDirty={passwordIsDirty}
                        isFocussed={passwordIsFocussed}
                      />
                    </div> */}
                  </Form.Item>
                  <Form.Item
                    label="Confirm password"
                    colon={false}
                    required={false}
                    name="confirm"
                    css={[s.inputItem()]}
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your password.",
                      },
                      {
                        validator: compareToFirstPassword,
                      },
                    ]}
                  >
                    <Input
                      type="password"
                      autoComplete="new-password"
                      onBlur={handleConfirmBlur}
                      data-cy="registerpage-input-password2"
                    />
                  </Form.Item>
                  <Form.Item
                    css={[s.inputItem()]}
                    colon={false}
                    required={false}
                    name="tos"
                    valuePropName="checked"
                    rules={[
                      {
                        required: true,
                        message: "Please read & accept the Terms of Service before completing registration.",
                        type: "boolean",
                        validator: validateToSAccepted,
                      },
                    ]}
                  >
                    <Checkbox data-cy="registerpage-input-tos" css={{ width: 325 }}>
                      I accept the <a href="/terms.html">Rhymewit Terms of Service</a>
                    </Checkbox>
                  </Form.Item>
                  {error ? (
                    <Form.Item label="Error">
                      <Alert
                        type="error"
                        message={`Registration failed`}
                        description={
                          <span>
                            {extractError(error).message}
                            {code ? (
                              <span>
                                {" "}
                                (Error code: <code>ERR_{code}</code>)
                              </span>
                            ) : null}
                          </span>
                        }
                      />
                    </Form.Item>
                  ) : null}
                  <Form.Item {...tailFormItemLayout} css={[{ display: "flex", alignItems: "center", marginTop: 75 }]}>
                    <Button
                      htmlType="submit"
                      size="large"
                      css={{ backgroundColor: colors.primary, color: colors.textInverted, width: 150, borderRadius: 5 }}
                      data-cy="registerpage-submit-button"
                    >
                      Sign up
                    </Button>
                  </Form.Item>
                </Form>
              </Row>
            ) : (
              <div>
                <Row justify="center" css={{ width: "100%" }}>
                  <span css={{ fontSize: "32px", fontWeight: "bold", marginBottom: 35, marginTop: 50 }}>
                    Let's get started!
                  </span>
                </Row>
                <Row align="middle" justify="center" css={{ width: "100%", marginBottom: 20 }}>
                  <span css={{ fontSize: "16px", fontWeight: 700, color: colors.secondary }}>
                    Choose your account type
                  </span>
                </Row>
                <Row align="middle" justify="center" css={{ width: "100%", marginTop: 10, textAlign: "center" }}>
                  <Radio.Group
                    data-cy="registerpage-input-type"
                    onChange={(e) => {
                      setAccountType(e.target.value);
                      pricingRef?.current?.scrollIntoView({ behavior: "smooth" });
                    }}
                    buttonStyle="solid"
                    size="large"
                  >
                    <Radio.Button value={UserAccountType.Educator} style={{ height: 290, width: 360, padding: 0 }}>
                      <div>
                        <Row justify="center" align="middle">
                          <Row
                            justify="center"
                            align="middle"
                            css={{
                              flexDirection: "column",
                              width: "100%",
                              color: "black",
                            }}
                          >
                            <span
                              css={[
                                s.accountTypeText,
                                accountType === UserAccountType.Educator ? { color: colors.secondary } : {},
                              ]}
                            >
                              Educator
                            </span>
                            <span
                              css={[
                                s.defaultText,
                                { fontWeight: 500, marginBottom: 20 },
                                accountType === UserAccountType.Educator ? { color: colors.secondary } : {},
                              ]}
                            >
                              For those who teach groups of students
                            </span>
                          </Row>

                          <Divider css={{ margin: "3px 6px", width: "90%", minWidth: "90%" }}></Divider>
                          <Row justify="center" align="middle" css={{ flexDirection: "column" }}>
                            <Row justify="start" align="middle" css={[s.accountInfoContainer, { marginTop: 10 }]}>
                              <div css={s.infoEmoji}>📚</div>
                              <div css={[s.accountInfoText]}>Access to Full Library of Rhymequizzes</div>
                            </Row>
                            <Row justify="start" align="middle" css={s.accountInfoContainer}>
                              <div css={s.infoEmoji}>⏩</div>
                              <div css={[s.accountInfoText]}>Play Quizzes at Normal, Slow, Slower Speeds</div>
                            </Row>
                            <Row justify="start" align="middle" css={s.accountInfoContainer}>
                              <div css={s.infoEmoji}>🔗</div>
                              <div css={[s.accountInfoText]}>Share Quizzes with your class</div>
                            </Row>
                            <Row justify="start" align="middle" css={s.accountInfoContainer}>
                              <div css={s.infoEmoji}>📊 </div>
                              <div css={[s.accountInfoText]}>Get Metrics on Class Performance</div>
                            </Row>
                            <Row justify="start" align="middle" css={s.accountInfoContainer}>
                              <div css={s.infoEmoji}>🏫 </div>
                              <div css={[s.accountInfoText]}>‍See standards alignment while exploring library</div>
                            </Row>
                          </Row>
                          {accountType === UserAccountType.Educator && (
                            <Row align="middle" justify="center" css={s.selectedIndicator}>
                              Selected
                            </Row>
                          )}
                        </Row>
                      </div>
                    </Radio.Button>
                    <Radio.Button
                      value={UserAccountType.General}
                      css={{
                        marginLeft: 0,
                        marginTop: 25,
                        height: "290px !important",
                        width: 360,
                        padding: 0,
                        [mq.md]: { marginLeft: 15 },
                      }}
                    >
                      <div>
                        <Row justify="center">
                          <Row
                            justify="center"
                            align="middle"
                            css={{
                              flexDirection: "column",
                              width: "100%",
                              color: "black",
                            }}
                          >
                            <span
                              css={[
                                s.accountTypeText,
                                accountType === UserAccountType.General ? { color: colors.secondary } : {},
                              ]}
                            >
                              Just For Fun
                            </span>
                            <span
                              css={[
                                s.defaultText,
                                { fontWeight: 500, marginBottom: 20 },
                                accountType === UserAccountType.General ? { color: colors.secondary } : {},
                              ]}
                            >
                              For individuals, guardians, and home use
                            </span>
                          </Row>

                          <Divider css={{ margin: "3px 6px", width: "90%", minWidth: "90%" }}></Divider>
                          <Row justify="center" align="middle" css={{ flexDirection: "column" }}>
                            <Row justify="start" align="middle" css={[s.accountInfoContainer, { marginTop: 10 }]}>
                              <div css={s.infoEmoji}>📚</div>
                              <div css={[s.accountInfoText]}>Access to Full Library of Rhymequizzes</div>
                            </Row>
                            <Row justify="start" align="middle" css={s.accountInfoContainer}>
                              <div css={s.infoEmoji}>⏩</div>
                              <div css={[s.accountInfoText]}>Play Quizzes at Normal, Slow, Slower Speeds</div>
                            </Row>
                            <Row justify="start" align="middle" css={s.accountInfoContainer}>
                              <div css={s.infoEmoji}>🏆</div>
                              <div css={[s.accountInfoText]}>Earn Badges & Rewards</div>
                            </Row>
                            <Row justify="start" align="middle" css={s.accountInfoContainer}>
                              <div css={s.infoEmoji}>🏋️</div>
                              <div css={[s.accountInfoText]}>Keeps track of your progress through all quizzes</div>
                            </Row>
                          </Row>
                          {accountType === UserAccountType.General && (
                            <Row align="middle" justify="center" css={[s.selectedIndicator, { marginTop: 50 }]}>
                              Selected
                            </Row>
                          )}
                        </Row>
                      </div>
                    </Radio.Button>
                  </Radio.Group>
                </Row>

                <Divider css={{ margin: "30px 0" }} />

                <div ref={pricingRef}>
                  <Pricing isShowTrialOption onPriceIdChange={(priceId) => setPriceId(priceId)} />
                </div>

                {showContinueBtn && (
                  <Row align="middle" justify="center">
                    <Button
                      size="large"
                      css={{
                        backgroundColor: colors.primary,
                        color: colors.textInverted,
                        width: 150,
                        borderRadius: 5,
                        marginTop: 40,
                        marginBottom: 16,
                        [mq.sm]: {
                          marginTop: 70,
                        },
                      }}
                      disabled={!accountType || !priceId}
                      data-cy="registerpage-submit-button"
                      onClick={() => {
                        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                        setShowRegisterForm(true);
                      }}
                    >
                      Continue
                    </Button>
                  </Row>
                )}
                <Row align="middle" justify="center" style={{ marginTop: !showContinueBtn ? 40 : 16 }}>
                  <div css={{ width: 210 }}>
                    <SocialLoginOptions
                      disabled={!accountType || !priceId}
                      buttonTextFromService={() => "Continue with Google"}
                      next="/personalize"
                      account_type={accountType}
                      pricing_option={priceId}
                    />
                  </div>
                </Row>
              </div>
            )}
          </div>
        )
      }
    </SharedLayout>
  );
};
Register.getInitialProps = async ({ query }) => ({
  next: typeof query.next === "string" ? query.next : null,
});

export default Register;

const s = {
  root: css({
    ".ant-form-item-label": {
      maxWidth: "100%",
    },
    ".ant-form-item-control": {
      maxWidth: "100%",
    },
    ".ant-radio-button-wrapper": {
      border: "1px solid #d9d9d9",
      "&:hover": {
        backgroundColor: "initial",
        boxShadow: "0px 0px 9px 3px rgba(95, 167, 245, 0.8)",
        color: "#5F5F5F",
      },
    },
    ".ant-radio-button-wrapper-checked": {
      background: "initial !important",
      boxShadow: "0px 0px 9px 3px rgba(95, 167, 245, 0.8)",
      color: `${colors.secondary} !important`,
      "&:hover": {
        background: "initial !important",
        boxShadow: "0px 0px 9px 3px rgba(95, 167, 245, 0.8)",
      },
      "&:focus": {
        background: "initial !important",
        boxShadow: "0px 0px 9px 3px rgba(95, 167, 245, 0.8)",
      },
    },
  }),
  accountTypeText: css({
    fontWeight: "bold",
    fontSize: "1.4rem",
    marginTop: 10,
    color: "#5F5F5F",
  }),
  defaultText: css({
    fontSize: 14,
    lineHeight: "10px",
    textAlign: "center",
    color: "#5F5F5F",
  }),
  accountInfoContainer: css({
    width: 300,
    marginBottom: 8,
  }),
  accountInfoText: css({
    fontSize: 12,
    maxWidth: 270,
    lineHeight: "22px",
    color: "#5f5f5f",
  }),
  infoEmoji: css({
    fontSize: 18,
    lineHeight: "22px",
    marginRight: 10,
  }),
  pricingRadioOption: css({
    width: 264,
    height: "130px !important",
    padding: 10,
    border: "1px solid #5f5f5f",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#5F5F5F",
  }),
  pricingMainText: css({
    fontSize: 24,
    fontWeight: "bold",
  }),
  pricingPriceText: css({
    fontSize: 18,
    fontWeight: 500,
  }),
  pricingSubText: css({
    fontSize: 14,
  }),
  selectedIndicator: css({
    position: "absolute",
    bottom: -10,
    height: 40,
    width: 360,
    background: colors.secondary,
    color: "white",
    boxShadow: "0px 3px 9px 3px rgba(95, 167, 245, 0.8)",
    border: `1px solid ${colors.secondary}`,
  }),
  pricingSelectedIndicator: css({ position: "absolute", top: 105, left: 0 }),
  inputItem: (isSmall?: boolean) =>
    css({
      display: "flex",
      fontSize: 16,
      flexDirection: "column",
      alignItems: "baseline",
      marginBottom: 25,
      maxWidth: 325,
      ".ant-input": {
        width: isSmall ? 150 : 325,
        fontSize: 16,
      },
      [mq.sm]: {
        marginBottom: 55,
      },
    }),
};
